<template lang="pug">
div
  div.d-flex.align-center
    div.text-xs.font-semibold.neutral500--text Invoice
    v-spacer
    y-button(v-if="invoice.notes" label="Read note" outlined color="neutral400" text-color="neutral700" size="xxs" prepend-icon="mdi-circle" icon-color="success500" icon-size="12" height="24" unset-min-width button-class="px-2" @click="showInvoiceNoteDialog = true")
    y-button(v-else label="Add note" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" @click="showInvoiceNoteDialog = true")
    y-button.ml-2(label="Download" outlined color="neutral400" text-color="neutral700" size="xxs" height="24" unset-min-width button-class="px-2" :loading="isLoadingDownloadInvoice" @click="onDownloadInvoice")
  v-card.mt-2(flat color="neutral200" :loading="isFetching ? 'primary' : false")
    div.d-flex.align-center.pa-2
      div.text-xs.font-medium.neutral500--text.no-wrap Bill to
      template(v-if="billingInfo")
        v-tooltip(top color="black" content-class="black tooltip-top-arrow" max-width="200")
          template(#activator="{ on, attrs }")
            div.text-xs.font-medium.neutral700--text.clickable.ellipsis-text-1.ml-3(v-bind="attrs" v-on="on") {{ billingInfo.name }}
          div
            div.text-xs.font-medium.neutral100--text {{ billingInfo.name }}
            div.text-xs.font-medium.neutral100--text.mt-1(v-if="hasFullAddress") {{ getAddressInString(billingInfo) }}
        div.text-xs.font-normal.neutral700--text.ml-2(v-if="billingInfo.phone") +{{ billingInfo.phone }}
        template(v-if="getRelationship(billingInfo)")
          v-chip.ml-2.px-1(x-small outlined color="blue500") {{ getRelationship(billingInfo) }}
      div.text-xs.font-medium.neutral700--text.ml-3(v-else) -
      v-spacer
      template(v-if="billingInfo")
        v-btn.ml-2(icon x-small @click="openEditBillingInfoDialog")
          v-icon(color="neutral700" size="16") $yzi-edit-05
        v-btn.ml-2(icon x-small  @click="showDeleteDialog = true")
          v-icon(color="danger500" size="18") $yzi-trash-03
      y-button(v-else label="Change" outlined color="neutral400" text-color="neutral700" background-color="var(--v-neutral100)" size="xxs" height="24" unset-min-width button-class="px-2" @click="openEditBillingInfoDialog")
  
  .dialogs
    invoice-note-dialog(v-if="showInvoiceNoteDialog" :open.sync="showInvoiceNoteDialog" @updated="fetchInvoice")
    download-invoice-receipt-dialog(v-if="showSelectInvoiceDialog" :open.sync="showSelectInvoiceDialog" :documents="invoiceTemplates" documentType="invoice")
    edit-billing-info-dialog(v-if="showEditBillingInfoDialog" :open.sync="showEditBillingInfoDialog" :parentBillingInfo="billingInfo" :parentPatient="patient" show-staff-id, @updated="fetchInvoice")
    delete-confirmation-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :loading="isLoading" @cancel="showDeleteDialog = false" @delete="removeBillingDetails")
      .text-lg.font-medium Are you sure you want to remove billing details?
      .text-md.mt-4 This action will be done immediately and can’t be undone. Click “Confirm” to proceed

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import InvoiceNoteDialog from '@/components/clinic/checkout/InvoiceNoteDialog.vue'
import DownloadInvoiceReceiptDialog from '@/components/clinic/dialogs/DownloadInvoiceReceiptDialog.vue'
import EditBillingInfoDialog from '@/components/clinic/dialogs/EditBillingInfoDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  components: { InvoiceNoteDialog, DownloadInvoiceReceiptDialog, EditBillingInfoDialog, DeleteConfirmationDialog },
  data () {
    return {
      isFetching: false,
      isLoading: false,
      isLoadingDownloadInvoice: false,
      showInvoiceNoteDialog: false,
      showSelectInvoiceDialog: false,
      showEditBillingInfoDialog: false,
      showDeleteDialog: false,
      invoiceTemplates: [],
    };
  },
  props: {},
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
      company: 'getCompany',
      leadPid: 'treatment/getLeadPid',
      invoice: 'treatment/getInvoice',
      patient: 'treatment/getPatient',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    billingInfo() {
      return this.invoice.billing_address
    },
    hasFullAddress() {
      return this.billingInfo && this.billingInfo.address_1
    },
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchInvoice() {
      this.isFetching = true
      this.$axios.get(`/invoices/${this.leadPid}`)
        .then(resp => {
          this.$store.commit('treatment/setInvoice', resp.data)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    openEditBillingInfoDialog() {
      this.showEditBillingInfoDialog = true
    },
    async onDownloadInvoice () {
      try {
        this.isLoadingDownloadInvoice = true
        let params = {
          company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
          status: 'active',
          categories: 'invoice'
        }
        const { data } = await this.$axios.get('/document_templates', { params })
        if (data.count > 1) {
          this.invoiceTemplates = data.results
          this.showSelectInvoiceDialog = true
          this.isLoadingDownloadInvoice = false
          return
        }
        if (data.count === 1) {
          this.onDownload(data.results[0])
          return
        }
        this.showMessage(['error', this.$t('error_message.create_item_first', { item: this.$t('invoice').toLowerCase() })])
        if (this.isHq) {
          return
        }
        this.$router.push('/admin/clinic/settings/document/templates')
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoadingDownloadInvoice = false
      }
    },
    async onDownload (item) {
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        this.isLoadingDownloadInvoice = false
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const url = `${baseUrl}/invoices/${this.leadPid}/preview_document?document_template=${item.id}&key=${key}`
      window.open(url,'_blank')
      this.isLoadingDownloadInvoice = false
    },
    removeBillingDetails() {
      this.isLoading = true
      const endpoint = `/addresses/${this.billingInfo.id}`
      this.$axios.delete(endpoint)
        .then(() => {
          this.updateActivityLog(endpoint, null, null, 'DELETE', this.billingInfo.id)
          this.fetchInvoice()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
          this.showDeleteDialog = false
        })
    },
    getAddressInString(addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    getRelationship (data) {
      if (data.extra_data && data.extra_data.relationship && data.extra_data.relationship.name) {
        return data.extra_data.relationship.name
      }
      return null
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      const data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'billing_info',
        activity: `Delete billing info (lead pid: ${this.leadPid})`,
        raw_data: { endpoint, payload, response, method}
      }
      this.$axios.post('/users/logs', data).catch(() => {})
    }
  },
};
</script>

<style lang="scss" scoped>
</style>